import {
  ClockCounterClockwise,
  LockKey,
  ShieldCheck,
} from '@phosphor-icons/react';
import { Stack, Text } from '@fragments-sdk/ui';
import { useQuery } from 'convex/react';
import { ContentState, StatusDashboardPage } from '@/components/layout';
import { api } from '@/convex';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import {
  FEATURE_DETAILS,
  PLANS,
  cheapestPlanWithFeature,
  hasFeature,
  type PlanId,
} from '../../../../convex/billing/plans';
import s from '@/page/governance/components/governance.module.scss';

type AuditEvent = {
  _id: string;
  action: string;
  resourceType: string;
  resourceId?: string;
  actorEmail?: string;
  description: string;
  createdAt: number;
};

type FindingAuditEvent = {
  _id: string;
  findingId: string;
  kind: string;
  actor?: string;
  metadata: Record<string, unknown>;
  createdAt: number;
  finding?: {
    fingerprint: string;
    code?: string;
    ruleId: string;
    filePath?: string;
    line?: number;
    status: string;
  };
};

type AuditRow =
  | ({ origin: 'audit' } & AuditEvent)
  | ({ origin: 'finding' } & FindingAuditEvent);

export function AuditLogPage() {
  const { orgId, org, isLoading } = useOrgContext();
  const { selectedProjectId, selectedProject } = useProjectContext();
  const plan = (org?.plan ?? 'free') as PlanId;
  const hasAuditLog = hasFeature(plan, 'auditLog');
  const auditRequiredPlan = cheapestPlanWithFeature('auditLog') ?? 'enterprise';
  const events = useQuery(
    api.auditLog.listByOrg,
    orgId && hasAuditLog ? { orgId, limit: 100 } : 'skip',
  ) as AuditEvent[] | undefined;
  const findingEvents = useQuery(
    api.findingsEvents.listByProject,
    selectedProjectId && hasAuditLog
      ? { projectId: selectedProjectId, limit: 100 }
      : 'skip',
  ) as FindingAuditEvent[] | undefined;
  const rows = mergeAuditRows(events, findingEvents);
  const loadingEvents =
    events === undefined ||
    (!!selectedProjectId && findingEvents === undefined);

  return (
    <StatusDashboardPage
      activeNav="Settings"
      className={s.statusDashboard}
      bodyClassName={s.statusBody}
      header={{
        title: 'Audit Log',
        description:
          'Append-only governance, policy, project, and member activity',
        meta: selectedProject?.name ?? org?.name,
      }}
    >
      {isLoading ? (
        <Text as="div" size="sm" color="tertiary" className={s.loadingState}>
          Loading audit events
        </Text>
      ) : !hasAuditLog ? (
        <ContentState
          minHeight={280}
          icon={<LockKey size={24} />}
          title={`${FEATURE_DETAILS.auditLog.label} is a ${PLANS[auditRequiredPlan].label} control`}
          description={`Your workspace is on the ${PLANS[plan].label} plan. Upgrade to ${PLANS[auditRequiredPlan].label} to review append-only activity history in Cloud.`}
        />
      ) : loadingEvents ? (
        <Text as="div" size="sm" color="tertiary" className={s.loadingState}>
          Loading audit events
        </Text>
      ) : rows.length === 0 ? (
        <ContentState
          minHeight={280}
          icon={<ShieldCheck size={24} />}
          title="No audit events yet"
          description="Activity will appear here as policies, suppressions, projects, and members change."
        />
      ) : (
        <Stack className={s.auditRows} gap="sm">
          {rows.map((event) => (
            <article key={event._id} className={s.auditRow}>
              <div className={s.auditIcon}>
                <ClockCounterClockwise size={14} />
              </div>
              <Stack className={s.auditBody} gap="xs">
                <div className={s.auditTitleRow}>
                  <Text as="strong" size="xs" weight="semibold">
                    {formatAuditRowAction(event)}
                  </Text>
                  <Text as="span" size="xs" color="tertiary">
                    {formatDateTime(event.createdAt)}
                  </Text>
                </div>
                <Text as="p" size="xs" color="secondary" className={s.auditDesc}>
                  {formatAuditRowDescription(event)}
                </Text>
                <Stack
                  direction="row"
                  wrap
                  gap="xs"
                  className={s.auditMeta}
                >
                  {auditRowMeta(event).map((item, index) =>
                    item.mono ? (
                      <Text as="code" size="2xs" key={index}>
                        {item.label}
                      </Text>
                    ) : (
                      <Text as="span" size="2xs" color="tertiary" key={index}>
                        {item.label}
                      </Text>
                    ),
                  )}
                </Stack>
              </Stack>
            </article>
          ))}
        </Stack>
      )}
    </StatusDashboardPage>
  );
}

function mergeAuditRows(
  auditEvents: AuditEvent[] | undefined,
  findingEvents: FindingAuditEvent[] | undefined,
): AuditRow[] {
  return [
    ...(auditEvents ?? []).map((event) => ({
      ...event,
      origin: 'audit' as const,
    })),
    ...(findingEvents ?? []).map((event) => ({
      ...event,
      origin: 'finding' as const,
    })),
  ].sort((a, b) => b.createdAt - a.createdAt || b._id.localeCompare(a._id));
}

function formatAuditRowAction(event: AuditRow): string {
  if (event.origin === 'audit') return formatAction(event.action);
  return formatAction(`finding.${event.kind}`);
}

function formatAuditRowDescription(event: AuditRow): string {
  if (event.origin === 'audit') return event.description;
  const finding = event.finding;
  const code = finding?.code ? `${finding.code} ` : '';
  const location = finding?.filePath
    ? ` in ${finding.filePath}${finding.line ? `:${finding.line}` : ''}`
    : '';
  return `${code}${finding?.ruleId ?? 'Finding'} was ${formatFindingEventKind(
    event.kind,
  )}${location}.`;
}

function auditRowMeta(
  event: AuditRow,
): Array<{ label: string; mono?: boolean }> {
  if (event.origin === 'audit') {
    return [
      { label: event.resourceType },
      ...(event.resourceId ? [{ label: event.resourceId, mono: true }] : []),
      ...(event.actorEmail ? [{ label: event.actorEmail }] : []),
    ];
  }
  return [
    { label: 'findingEvent' },
    { label: event.findingId, mono: true },
    ...(event.actor ? [{ label: event.actor }] : []),
    ...(event.finding?.status ? [{ label: event.finding.status }] : []),
  ];
}

function formatFindingEventKind(kind: string): string {
  if (kind === 'suppressed') return 'suppressed';
  if (kind === 'reopened') return 'reopened';
  if (kind === 'fixed') return 'fixed';
  if (kind === 'commented') return 'commented on';
  if (kind === 'dismissed') return 'dismissed';
  if (kind === 'shown') return 'shown';
  return 'produced';
}

function formatAction(action: string): string {
  return action
    .split('.')
    .map((part) => part.replace(/_/g, ' '))
    .join(' · ');
}

function formatDateTime(value: number): string {
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
